import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../store/authstore';
import { ROUTES } from './routes';
import { RoleType } from '../types/RoleType';

// Define interface for props
interface PrivateRouteProps {
  children: React.ReactNode;
  roleType?: RoleType; // Optional role type (e.g., 'admin', 'user')
  roles?: string[]; // Optional array of allowed roles
  requiredPermission?: string; // Optional required permission
}

// PrivateRoute component
const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  roleType,
  roles,
  requiredPermission,
}) => {
  const { isAuthenticated, hasPermission, hasRole, hasRoleType, hasHydrated } = useAuthStore();
  const location = useLocation();

  // Wait for zustand's persist rehydration before making an auth decision —
  // otherwise the default isAuthenticated:false briefly redirects to login
  // even when a valid session is stored in sessionStorage. This mounts on every
  // route change (each route is individually wrapped), but hasHydrated only
  // starts false once per page load, so this only ever shows on first load.
  if (!hasHydrated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // Check role type if provided
  if (roleType && !hasRoleType(roleType)) {
    return <Navigate to={ROUTES.ERROR.UNAUTHORIZED} replace />;
  }

  // Check specific roles if provided
  if (roles && !roles.some((role) => hasRole(role))) {
    return <Navigate to={ROUTES.ERROR.UNAUTHORIZED} replace />;
  }

  // Check required permission if provided
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={ROUTES.ERROR.UNAUTHORIZED} replace />;
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default PrivateRoute;
